import { IssueCaseStatusCode, IssueLiabilityDoc, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { isAfter, isBefore, startOfDay, startOfToday, subDays } from 'date-fns';
import { chunk } from 'lodash';
import { useEffect, useState } from 'react';
import { combineLatest, of, switchMap } from 'rxjs';
import { ISSUE_CASE_ACTIVE_STATUSES } from 'src/constants/issue.constants';

import { formatDate } from 'src/utils/date-util';
import { observeIssueCases, observeIssueLiabilities, observeIssueTasks } from 'src/utils/firebase/firebase-short-cut';
import { processIssueData } from 'src/utils/issue/issue-data-util';

import { IssueManageMasterRowData } from '../../components/Issue/IssueManage/IssueManage.types';

export const useIssueManageData = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(startOfToday());
  const [selectedStatuses, setSelectedStatuses] = useState<IssueCaseStatusCode[]>(ISSUE_CASE_ACTIVE_STATUSES);
  const [rowData, setRowData] = useState<IssueManageMasterRowData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Date restriction logic
  const minDate = subDays(startOfToday(), 40);
  const maxDate = startOfToday();

  const handleDateChange = (date: Date | null) => {
    if (!date) return;

    // Enforce constraints
    let newDate = startOfDay(date);
    if (isBefore(newDate, minDate)) newDate = minDate;
    if (isAfter(newDate, maxDate)) newDate = maxDate;

    setSelectedDate(newDate);
  };

  useEffect(() => {
    setIsLoading(true);

    // Query for cases created on or after the selected date
    // IssueCase.registeredAt is a string formatted as "yyyy-MM-dd'T'HH:mm:ss+0900"
    const formattedDate = formatDate(selectedDate, "yyyy-MM-dd'T'HH:mm:ss+0900");

    const casesObservable = observeIssueCases([['registeredAt', '>=', formattedDate]]);

    // issueCase 조회 후 해당 caseId들로 issueTask, issueLiability 조회
    const subscription = casesObservable
      .pipe(
        switchMap((cases) => {
          const filteredCases = cases.filter((issueCase) => selectedStatuses.includes(issueCase.status));
          const caseIds = filteredCases.map((c) => c._id);

          if (caseIds.length === 0) {
            return of({ cases: filteredCases, tasks: [] as IssueTaskDoc[], liabilities: [] as IssueLiabilityDoc[] });
          }

          // Firestore "in" 쿼리는 최대 30개 제한이므로 chunk로 나눔
          const caseIdChunks = chunk(caseIds, 30);
          const taskObservables = caseIdChunks.map((ids) => observeIssueTasks([['issueCaseId', 'in', ids]]));
          const liabilityObservables = caseIdChunks.map((ids) => observeIssueLiabilities([['issueCaseId', 'in', ids]]));

          return combineLatest([combineLatest(taskObservables), combineLatest(liabilityObservables)]).pipe(
            switchMap(([taskArrays, liabilityArrays]) => {
              const allTasks = taskArrays.flat();
              const allLiabilities = liabilityArrays.flat().filter((liability) => !liability.deletedAt);
              return of({ cases: filteredCases, tasks: allTasks, liabilities: allLiabilities });
            })
          );
        })
      )
      .subscribe({
        next: ({ cases, tasks, liabilities }) => {
          const processedData = processIssueData(cases, tasks, liabilities);
          setRowData(processedData);
          setIsLoading(false);
        },
        error: (err) => {
          console.error('Failed to fetch issues', err);
          setIsLoading(false);
        },
      });

    return () => subscription.unsubscribe();
  }, [selectedDate, selectedStatuses]);

  return {
    selectedDate,
    handleDateChange,
    selectedStatuses,
    setSelectedStatuses,
    rowData,
    isLoading,
    minDate,
    maxDate,
  };
};
